import React, { useState } from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Switch,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import dayjs from "dayjs";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useApiQuery } from "src/hooks/useApiQuery";
import type { IAccountData } from "src/Types/account.types";
import type { ICreateSessionEnrollPayload } from "src/Types/module/b2bSession/b2bSessionEnroll.types";
import B2BSearchSelect from "../SessionRequest/B2BSearchSelect";
import SessionMultiSelectField from "./SessionMultiSelectField";

interface Props {
	open: boolean;
	handleClose: () => void;
}

const CreateSessionEnrollModal: React.FC<Props> = ({ open, handleClose }) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

	const [b2bId, setB2bId] = useState<number | null>(null);
	const [sessionIds, setSessionIds] = useState<number[]>([]);
	const [createInvoice, setCreateInvoice] = useState<boolean>(true);
	const [invoiceDiscount, setInvoiceDiscount] = useState<string>("0");
	const [withMoneyReceipt, setWithMoneyReceipt] = useState<boolean>(true);
	const [paymentMethod, setPaymentMethod] = useState<string>("Cash");
	const [accountId, setAccountId] = useState<number | "">("");
	const [paidAmount, setPaidAmount] = useState<string>("");
	const [paymentDate, setPaymentDate] = useState<string>(
		dayjs().format("YYYY-MM-DD")
	);
	const [note, setNote] = useState<string>("");
	const [formError, setFormError] = useState<string | null>(null);

	// Accounts query based on payment method
	const { data: rawAccounts = [], isLoading: isLoadingAccounts } = useApiQuery<
		IAccountData[]
	>({
		endPoint: `/api/fair/admin/account`,
		queryKeys: ["Accounts", paymentMethod],
		params: { ac_type: paymentMethod },
	});

	const accounts = React.useMemo(() => {
		if (Array.isArray(rawAccounts)) return rawAccounts;
		if (Array.isArray((rawAccounts as any)?.data)) return (rawAccounts as any).data;
		return [];
	}, [rawAccounts]);

	const { mutate: enrollSessions, isPending } = useApiMutation<
		ICreateSessionEnrollPayload,
		any
	>({
		method: "POST",
		endPoint: "/api/v1/fair/admin/session/b2b-sessions",
		invalidateQueries: [
			"SessionEnrolls",
			"SessionEnroll",
			"b2b-sessions",
			"b2b_details",
			"B2B",
		],
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setFormError(null);

		if (!b2bId) {
			setFormError("Please select a B2B member.");
			return;
		}

		if (sessionIds.length === 0) {
			setFormError("Please select at least one session slot.");
			return;
		}

		if (createInvoice && withMoneyReceipt) {
			if (!accountId) {
				setFormError("Please select a payment account.");
				return;
			}
			if (!paidAmount || Number(paidAmount) <= 0) {
				setFormError("Please enter a valid paid amount.");
				return;
			}
		}

		const payload: ICreateSessionEnrollPayload = {
			b2b_id: b2bId,
			session_ids: sessionIds,
			create_invoice: createInvoice,
			...(createInvoice
				? {
					invoice_discount:
						invoiceDiscount !== "" ? Number(invoiceDiscount) : 0,
					with_money_receipt: withMoneyReceipt,
					...(withMoneyReceipt
						? {
							account_id: Number(accountId),
							paid_amount: Number(paidAmount) || 0,
							payment_method: paymentMethod,
							payment_date: paymentDate,
						}
						: {}),
				}
				: {}),
			...(note.trim() ? { note: note.trim() } : {}),
		};

		enrollSessions(payload, {
			onSuccess: () => {
				handleModalClose();
			},
			onError: (err: any) => {
				console.error(err);
				setFormError(err?.message || "Failed to enroll session.");
			},
		});
	};

	const handleModalClose = () => {
		setB2bId(null);
		setSessionIds([]);
		setCreateInvoice(true);
		setInvoiceDiscount("0");
		setWithMoneyReceipt(true);
		setPaymentMethod("Cash");
		setAccountId("");
		setPaidAmount("");
		setPaymentDate(dayjs().format("YYYY-MM-DD"));
		setNote("");
		setFormError(null);
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleModalClose}
			fullScreen={fullScreen}
			maxWidth="md"
			fullWidth
		>
			<DialogTitle
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 3,
					py: 2,
				}}
			>
				<Typography variant="h5" fontWeight={700}>
					Enroll B2B Session
				</Typography>
				<IconButton onClick={handleModalClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<Divider />

			<Box component="form" onSubmit={handleSubmit}>
				<DialogContent sx={{ p: 3 }}>
					{formError && (
						<Alert severity="error" sx={{ mb: 2.5 }}>
							{formError}
						</Alert>
					)}

					<Grid container spacing={2.5}>
						{/* B2B Member */}
						<Grid item xs={12}>
							<B2BSearchSelect
								label="Select B2B Member *"
								placeholder="Search B2B member by name, email, organization..."
								value={b2bId}
								onChange={(id) => setB2bId(id)}
								required
							/>
						</Grid>

						{/* Sessions */}
						<Grid item xs={12}>
							<SessionMultiSelectField
								label="Select Session(s) *"
								value={sessionIds}
								onChange={(ids) => setSessionIds(ids)}
								required
							/>
						</Grid>

						{/* Invoice Section Card */}
						<Grid item xs={12}>
							<Card
								variant="outlined"
								sx={{
									p: 2,
									boxShadow: "none",
									bgcolor: (theme) =>
										theme.palette.mode === "dark" ? "background.paper" : "#F8FAFC",
									borderColor: "divider",
									borderRadius: 1.5,
								}}
							>
								<Box
									display="flex"
									alignItems="center"
									justifyContent="space-between"
									mb={createInvoice ? 2 : 0}
								>
									<Box display="flex" alignItems="center" gap={1}>
										<ReceiptLongOutlinedIcon
											sx={{ color: "primary.main", fontSize: 22 }}
										/>
										<Typography variant="subtitle1" fontWeight={700}>
											Invoice Configuration
										</Typography>
									</Box>
									<FormControlLabel
										control={
											<Switch
												checked={createInvoice}
												onChange={(e) => setCreateInvoice(e.target.checked)}
												color="primary"
											/>
										}
										label={
											<Typography variant="body2" fontWeight={600}>
												Create Invoice
											</Typography>
										}
										sx={{ m: 0 }}
									/>
								</Box>

								{createInvoice && (
									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<TextField
												fullWidth
												size="small"
												label="Invoice Discount (৳)"
												type="number"
												InputLabelProps={{ shrink: true }}
												value={invoiceDiscount}
												onChange={(e) => setInvoiceDiscount(e.target.value)}
												placeholder="0"
											/>
										</Grid>

										<Grid item xs={12} sm={6}>
											<Box
												display="flex"
												alignItems="center"
												height="100%"
												pl={1}
											>
												<FormControlLabel
													control={
														<Switch
															checked={withMoneyReceipt}
															onChange={(e) =>
																setWithMoneyReceipt(e.target.checked)
															}
															color="primary"
														/>
													}
													label={
														<Typography variant="body2" fontWeight={600}>
															With Money Receipt
														</Typography>
													}
												/>
											</Box>
										</Grid>

										{/* Money Receipt Sub-section */}
										{withMoneyReceipt && (
											<Grid item xs={12}>
												<Card
													variant="outlined"
													sx={{
														p: 2,
														boxShadow: "none",
														bgcolor: (theme) =>
															theme.palette.mode === "dark"
																? "rgba(255,255,255,0.02)"
																: "#FFFFFF",
														borderColor: "divider",
														borderRadius: 1.5,
													}}
												>
													<Box display="flex" alignItems="center" gap={1} mb={2}>
														<PaymentsOutlinedIcon
															sx={{ color: "primary.main", fontSize: 20 }}
														/>
														<Typography
															variant="subtitle2"
															fontWeight={700}
															color="primary.main"
														>
															Money Receipt Details
														</Typography>
													</Box>

													<Grid container spacing={2}>
														{/* Payment Method */}
														<Grid item xs={12} sm={6}>
															<FormControl fullWidth size="small">
																<InputLabel id="payment-method-label" shrink>
																	Payment Method
																</InputLabel>
																<Select
																	labelId="payment-method-label"
																	value={paymentMethod}
																	label="Payment Method"
																	notched
																	onChange={(e) => {
																		setPaymentMethod(e.target.value);
																		setAccountId("");
																	}}
																>
																	<MenuItem value="Cash">Cash</MenuItem>
																	<MenuItem value="Bank">Bank</MenuItem>
																	<MenuItem value="Mobile Banking">
																		Mobile Banking
																	</MenuItem>
																</Select>
															</FormControl>
														</Grid>

														{/* Account */}
														<Grid item xs={12} sm={6}>
															<FormControl fullWidth size="small">
																<InputLabel id="account-select-label" shrink>
																	Account *
																</InputLabel>
																<Select
																	labelId="account-select-label"
																	value={accountId}
																	label="Account *"
																	notched
																	onChange={(e) =>
																		setAccountId(Number(e.target.value))
																	}
																>
																	<MenuItem value="" disabled>
																		<em>
																			{isLoadingAccounts
																				? "Loading accounts..."
																				: "Select Account"}
																		</em>
																	</MenuItem>
																	{accounts.map((acc) => (
																		<MenuItem key={acc.ac_id} value={acc.ac_id}>
																			{acc.ac_bank_name || acc.ac_name} (
																			{acc.ac_name})
																		</MenuItem>
																	))}
																</Select>
															</FormControl>
														</Grid>

														{/* Paid Amount */}
														<Grid item xs={12} sm={6}>
															<TextField
																fullWidth
																size="small"
																label="Paid Amount (৳) *"
																type="number"
																InputLabelProps={{ shrink: true }}
																value={paidAmount}
																onChange={(e) => setPaidAmount(e.target.value)}
																placeholder="e.g. 500"
																required
															/>
														</Grid>

														{/* Payment Date */}
														<Grid item xs={12} sm={6}>
															<TextField
																fullWidth
																size="small"
																label="Payment Date *"
																type="date"
																InputLabelProps={{ shrink: true }}
																value={paymentDate}
																onChange={(e) =>
																	setPaymentDate(e.target.value)
																}
																required
															/>
														</Grid>
													</Grid>
												</Card>
											</Grid>
										)}
									</Grid>
								)}
							</Card>
						</Grid>

						{/* Note */}
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Note"
								placeholder="e.g. Enrolled with discount & partial payment"
								multiline
								rows={2.5}
								size="small"
								InputLabelProps={{ shrink: true }}
								value={note}
								onChange={(e) => setNote(e.target.value)}
							/>
						</Grid>
					</Grid>
				</DialogContent>

				<Divider />

				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button
						variant="outlined"
						onClick={handleModalClose}
						disabled={isPending}
					>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={isPending}>
						{isPending ? "Enrolling..." : "Enroll Session"}
					</Button>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

export default CreateSessionEnrollModal;
